import { createContext, useContext } from 'react'
import type { MatterAssetFeatureConfig } from './matter-asset-feature-config'

/**
 * Scope for the shared matter-asset "living asset" components.
 *
 * Carries both the per-feature config (which data wiring/columns/copy to use) and the store key
 * (the living asset id the surrounding shell owns). The shell renders these components keyed by
 * that asset id and owns data loading; this context carries both down so the shared components
 * stay feature-agnostic. Supplied via MatterAssetFeatureProvider. Supersedes the med-chron-only
 * events scope context.
 */
export type MatterAssetFeatureScope = {
  config: MatterAssetFeatureConfig
  // Store key into the shared events/references slices (the living asset id).
  storeKey: string
}

export const MatterAssetFeatureContext = createContext<MatterAssetFeatureScope | null>(null)

/** The active feature config. Throws outside a provider (a setup bug, not a runtime state). */
export function useMatterAssetFeatureConfig(): MatterAssetFeatureConfig {
  const scope = useContext(MatterAssetFeatureContext)
  if (!scope) {
    throw new Error('useMatterAssetFeatureConfig must be used within a MatterAssetFeatureProvider')
  }
  return scope.config
}

/** Store key for events/references: the living asset id, or undefined when no provider is present. */
export function useMatterAssetStoreKey(): string | undefined {
  return useContext(MatterAssetFeatureContext)?.storeKey
}
